import type { GameEnvironment, Player } from "../game";
import type { ConnectFourState } from "./types";

export const CONNECT_FOUR_ROWS = 6;
export const CONNECT_FOUR_COLUMNS = 7;
export const CONNECT_FOUR_ACTIONS = CONNECT_FOUR_COLUMNS;
export const CONNECT_FOUR_MAX_STEPS =
  CONNECT_FOUR_ROWS * CONNECT_FOUR_COLUMNS;

function emptyBoard(): Int8Array {
  return new Int8Array(CONNECT_FOUR_MAX_STEPS).fill(-1);
}

function boardIndex(row: number, column: number): number {
  return row * CONNECT_FOUR_COLUMNS + column;
}

function hasFour(board: Int8Array, player: Player): boolean {
  const directions = [
    [0, 1],
    [1, 0],
    [1, 1],
    [1, -1],
  ] as const;
  for (let row = 0; row < CONNECT_FOUR_ROWS; row += 1) {
    for (let column = 0; column < CONNECT_FOUR_COLUMNS; column += 1) {
      if (board[boardIndex(row, column)] !== player) continue;
      for (const [dr, dc] of directions) {
        const endRow = row + dr * 3;
        const endColumn = column + dc * 3;
        if (
          endRow < 0 ||
          endRow >= CONNECT_FOUR_ROWS ||
          endColumn < 0 ||
          endColumn >= CONNECT_FOUR_COLUMNS
        ) {
          continue;
        }
        let matches = true;
        for (let offset = 1; offset < 4; offset += 1) {
          if (
            board[boardIndex(row + dr * offset, column + dc * offset)] !==
            player
          ) {
            matches = false;
            break;
          }
        }
        if (matches) return true;
      }
    }
  }
  return false;
}

export class PgxConnectFour implements GameEnvironment<ConnectFourState> {
  init(): ConnectFourState {
    return {
      currentPlayer: 0,
      board: emptyBoard(),
      stepCount: 0,
      moves: [],
      lastAction: -1,
      winner: null,
      terminated: false,
      truncated: false,
      terminalReason: null,
      rewards: [0, 0],
    };
  }

  step(state: ConnectFourState, action: number): ConnectFourState {
    if (
      state.terminated ||
      state.truncated ||
      !Number.isInteger(action) ||
      action < 0 ||
      action >= CONNECT_FOUR_ACTIONS ||
      state.board[action] !== -1
    ) {
      const rewards: [number, number] = [0, 0];
      rewards[state.currentPlayer] = -1;
      rewards[(1 - state.currentPlayer) as Player] = 1;
      return {
        ...state,
        terminated: true,
        terminalReason: "illegal-action",
        rewards,
      };
    }

    const board = state.board.slice();
    let row = CONNECT_FOUR_ROWS - 1;
    while (row > 0 && board[boardIndex(row, action)] !== -1) row -= 1;
    board[boardIndex(row, action)] = state.currentPlayer;
    const won = hasFour(board, state.currentPlayer);
    const stepCount = state.stepCount + 1;
    const draw = !won && stepCount >= CONNECT_FOUR_MAX_STEPS;
    const rewards: [number, number] = [0, 0];
    if (won) {
      rewards[state.currentPlayer] = 1;
      rewards[(1 - state.currentPlayer) as Player] = -1;
    }
    return {
      currentPlayer: (1 - state.currentPlayer) as Player,
      board,
      stepCount,
      moves: [
        ...state.moves,
        { action, label: this.formatAction(state, action) },
      ],
      lastAction: action,
      winner: won ? state.currentPlayer : null,
      terminated: won || draw,
      truncated: false,
      terminalReason: won ? "win" : draw ? "draw" : null,
      rewards,
    };
  }

  observe(state: ConnectFourState): Float32Array {
    const observation = new Float32Array(
      CONNECT_FOUR_ROWS * CONNECT_FOUR_COLUMNS * 3,
    );
    for (let cell = 0; cell < state.board.length; cell += 1) {
      const base = cell * 3;
      observation[base] = state.board[cell] === state.currentPlayer ? 1 : 0;
      observation[base + 1] =
        state.board[cell] === 1 - state.currentPlayer ? 1 : 0;
      observation[base + 2] = 1;
    }
    return observation;
  }

  legalActionMask(state: ConnectFourState): Uint8Array {
    const mask = new Uint8Array(CONNECT_FOUR_ACTIONS);
    // PGX exposes an all-true terminal mask; search bypasses terminal states.
    if (state.terminated || state.truncated) return mask.fill(1);
    for (let column = 0; column < CONNECT_FOUR_COLUMNS; column += 1) {
      mask[column] = state.board[column] === -1 ? 1 : 0;
    }
    return mask;
  }

  currentPlayer(state: ConnectFourState): Player {
    return state.currentPlayer;
  }

  isTerminal(state: ConnectFourState): boolean {
    return state.terminated || state.truncated;
  }

  terminalValue(state: ConnectFourState): number {
    return state.rewards[state.currentPlayer] ?? 0;
  }

  formatAction(_state: ConnectFourState, action: number): string {
    return `Column ${action + 1}`;
  }
}

export const connectFour = new PgxConnectFour();
